import { defineStore } from 'pinia';
import type { Right, Role, Token } from '@taxi/shared';

const STORAGE_KEY = 'taxi_token_v1';

export interface TokenState {
	token: Token | null;
	/** Vrai tant que la restauration depuis le stockage local n'a pas eu lieu. */
	loading: boolean;
}

/**
 * Jeton d'authentification et droits effectifs.
 *
 * Persisté en `localStorage` pour survivre à la fermeture de l'application — la
 * borne comme le mobile chauffeur restent ouverts très longtemps. Une
 * déconnexion est propagée aux autres onglets par `BroadcastChannel`.
 */
export const useTokenStore = defineStore('token', {
	state: (): TokenState => ({
		token: null,
		loading: true,
	}),

	getters: {
		isAuthenticated: (state): boolean => Boolean(state.token?.accessToken),
		isGuest: (state): boolean => state.token?.isGuest ?? false,
		isKiosk: (state): boolean => state.token?.kioskId != null,
		rights: (state): Right[] => state.token?.rights ?? [],
		roles: (state): Role[] => state.token?.roles ?? [],
		userId: (state): number | null => state.token?.userId ?? null,
	},

	actions: {
		/** Restaure la session et se met à l'écoute des déconnexions des autres onglets. */
		setup(): void {
			if (typeof window === 'undefined') {
				this.loading = false;
				return;
			}
			try {
				const stored = window.localStorage.getItem(STORAGE_KEY);
				this.token = stored ? (JSON.parse(stored) as Token) : null;
			} catch {
				this.token = null;
			}
			this.loading = false;

			try {
				const channel = new BroadcastChannel('taxi-logout');
				channel.onmessage = () => {
					this.token = null;
					window.localStorage.removeItem(STORAGE_KEY);
				};
			} catch {
				// BroadcastChannel indisponible (WebView ancienne) : sans conséquence.
			}
		},

		setToken(token: Token | null): void {
			this.token = token;
			if (typeof window === 'undefined') {
				return;
			}
			if (token) {
				window.localStorage.setItem(STORAGE_KEY, JSON.stringify(token));
			} else {
				window.localStorage.removeItem(STORAGE_KEY);
			}
		},

		/**
		 * Rafraîchit le jeton d'accès.
		 *
		 * Appelé par le `Caller` sur un 401. En cas d'échec, la session est purgée :
		 * l'application repartira sur l'écran de connexion plutôt que de boucler.
		 */
		async refresh(): Promise<boolean> {
			const refreshToken = this.token?.refreshToken;
			if (!refreshToken) {
				this.setToken(null);
				return false;
			}
			try {
				const response = await fetch(`${this.apiBaseUrl}/auth/refresh`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ refreshToken }),
				});
				if (!response.ok) {
					this.setToken(null);
					return false;
				}
				this.setToken((await response.json()) as Token);
				return true;
			} catch {
				return false;
			}
		},

		logout(): void {
			this.setToken(null);
			try {
				new BroadcastChannel('taxi-logout').postMessage('logout');
			} catch {
				// Sans conséquence.
			}
		},
	},
});

declare module 'pinia' {
	export interface PiniaCustomProperties {
		apiBaseUrl: string;
	}
}
